import {ChangeDetectionStrategy, Component, computed, inject, input, InputSignal, Signal} from "@angular/core";
import {NavigationService} from "../../navigation/navigation.service";
import {CurrencyPipe} from '../../utils/currency.pipe';
import {NgClass} from '@angular/common';
import {TranslateModule} from '@ngx-translate/core';
import {ButtonComponent} from "../../buttons/button/button.component";
import {ButtonProminence} from "../../buttons/button/button";
import {OrderingContextIntention} from '../../ordering-context/ordering-context';
import {OrderingContextModel} from '../../ordering-context/ordering-context.model';
import {OrderModel} from "../order.model";
import {RecentOrderData} from "../recent-order.service";
import {PaymentType} from '../../payment/payment';
import {OrderItemData, PaymentSurchargeData} from "../order-initiation.service";
import {OrderType} from '../order';
import {CardBrandComponent} from "../../ui-elements/card-brand/card-brand.component";
import {IconComponent} from "../../ui-elements/icon/icon.component";
import {QrCodePanelComponent} from "../../ui-elements/qr-code/qr-code-panel/qr-code-panel.component";
import {StoreDetailsPanelComponent} from '../../store/store-details-panel/store-details-panel.component';
import {FieldGroupComponent} from '../../inputs/form/field-group/field-group.component';
import {OrderExtrasPanelComponent} from "../order-extras-panel/order-extras-panel.component";
import {OrderTotalsPanelComponent} from "../order-totals-panel/order-totals-panel.component";
import {PossessivePipe} from '../../utils/possessive.pipe';
import {HtmlPipe} from '../../utils/html.pipe';
import {ConfigurationModel} from '../../configuration/configuration.model';

export class TransferRequestDetails {
    public readonly name: string | undefined;
    public readonly heading: string;
    public readonly subtotalLabel: string;

    constructor(public transferRequestId: string, public transferRequestName: string, public items: OrderItemData[], public subTotal: number) {
        this.name = this.transferRequestId ? new PossessivePipe().transform(this.transferRequestName) : undefined;
        this.heading = this.transferRequestId ? "woa.heading.order.summary.transferred" : "woa.heading.order.summary.local";
        this.subtotalLabel = this.transferRequestId ? "woa.label.order.summary.subtotal.transferred" : "woa.label.order.summary.subtotal.local";
    }
}

@Component({
    selector: "woa-order-details-panel",
    templateUrl: "./order-details-panel.component.html",
    styleUrls: ["./order-details-panel.component.scss"],
    imports: [
        ButtonComponent,
        CardBrandComponent,
        CurrencyPipe,
        FieldGroupComponent,
        HtmlPipe,
        IconComponent,
        NgClass,
        OrderExtrasPanelComponent,
        OrderTotalsPanelComponent,
        PossessivePipe,
        QrCodePanelComponent,
        StoreDetailsPanelComponent,
        TranslateModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})

export class OrderDetailsPanelComponent {
    public orderingContextModel: OrderingContextModel = inject(OrderingContextModel);
    public orderModel: OrderModel = inject(OrderModel);
    public configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private navigationService: NavigationService = inject(NavigationService);

    OrderType = OrderType;
    PaymentType = PaymentType;
    ButtonProminence = ButtonProminence;
    OrderingContextIntention = OrderingContextIntention;

    public readonly recentOrder: InputSignal<RecentOrderData> = input.required<RecentOrderData>();

    public readonly showFulfilmentDetails: InputSignal<boolean> = input<boolean>(true);
    public readonly showLocationDetails: InputSignal<boolean> = input<boolean>(true);
    public readonly showOrderDetails: InputSignal<boolean> = input<boolean>(true);
    public readonly showPayment: InputSignal<boolean> = input<boolean>(true);

    public readonly memberAvailableRedemptionPoints: Signal<number> = computed<number>(() =>
        this.recentOrder()?.orderPlacement.paymentAuthorisation.payWithPoints
            ? this.recentOrder().initiatedOrder.memberAvailableRedemptionPoints
            : undefined);

    public readonly transferRequestDetails: Signal<TransferRequestDetails[]> = computed<TransferRequestDetails[]>(() =>
        this.recentOrder()
            ? this.recentOrder().orderInitiation.items
                .filter((item: OrderItemData, index: number, items: OrderItemData[]) => items.findIndex((r: OrderItemData) => r.transferRequestId === item.transferRequestId) === index)
                .sort((a: OrderItemData, b: OrderItemData) => (a.transferRequestName || "").localeCompare(b.transferRequestName || ""))
                .map((item: OrderItemData) => new TransferRequestDetails(
                    item.transferRequestId, item.transferRequestName,
                    this.recentOrder().orderInitiation.getItemsForTransferRequest(item.transferRequestId),
                    this.recentOrder().orderInitiation.getSubtotalForTransferRequest(item.transferRequestId)))
            : []);

    public readonly totalTransfers: Signal<number> = computed<number>(() => this.recentOrder()?.orderInitiation.totalTransfers);

    public readonly totalPayment: Signal<number> = computed<number>(() =>
        this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints ? this.recentOrder().initiatedOrder.orderTotal
            : this.recentOrder().initiatedOrder.getOrderOrderTotalWithPaymentSurcharge(PaymentSurchargeData.CREDIT_CARD_TYPE));

    public readonly paymentMethod: Signal<string> = computed(() =>
        this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.POINTS ? "woa.label.order.pointsPayment"
            : this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.SAVED_CREDIT_CARD || this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.NEW_CREDIT_CARD ?
                (this.recentOrder().orderPlacement.paymentAuthorisation.paymentCardLast4 ? "woa.label.order.creditCardPayment"
                    : "woa.label.order.unknownCreditCardPayment")
                : this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.TAP_TO_PAY ?
                    (!this.recentOrder().orderPlacement.paymentAuthorisation.paymentCardLast4 ? "woa.label.order.tapToPayPayment"
                        : !this.recentOrder().orderPlacement.paymentAuthorisation.paymentCardNetwork ? "woa.label.order.tapToPayCardPayment"
                            : this.recentOrder().orderPlacement.paymentAuthorisation.paymentCardNetwork === "eftpos_au" ? "woa.label.order.tapToPayEftposPayment"
                                : "woa.label.order.tapToPayCreditCardPayment")
                    : this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.GOOGLE_PAY ? "woa.label.order.googlePayPayment"
                        : this.recentOrder().orderPlacement.paymentAuthorisation.paymentType === PaymentType.APPLE_PAY ? "woa.label.order.applePayPayment"
                            : this.recentOrder().orderPlacement.paymentAuthorisation.payWithPoints ? "woa.label.order.pointsPayment"
                                : this.recentOrder().orderPlacement.paymentAuthorisation.paymentCardLast4 ? "woa.label.order.creditCardPayment"
                                    : "woa.label.order.unknownCreditCardPayment");

    public readonly orderTimeLabel: Signal<string> = computed(() =>
        this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? (this.recentOrder().placedOrder.requestId && this.recentOrder().placedOrder.placedDateTime ? "woa.label.order.details.tableOrderTime" : undefined)
            : !this.orderingContextModel.canShowETA() ? (this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.asapPickupTime" : "woa.label.order.details.asapDeliveryTime")
                : this.recentOrder().initiatedOrder.orderLaterDateTime ? (this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.scheduledPickupTime" : "woa.label.order.details.scheduledDeliveryTime")
                    : this.recentOrder().placedOrder.requestId ? (this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD ? "woa.label.order.details.estimatedPickupTime" : "woa.label.order.details.estimatedDeliveryTime")
                        : undefined);

    public readonly orderTime: Signal<string> = computed(() =>
        this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AT_TABLE ? this.orderModel.formatPlacedDateTime(this.recentOrder().placedOrder)
            : this.recentOrder().initiatedOrder.orderLaterDateTime ? (this.recentOrder().placedOrder && this.recentOrder().placedOrder.estimatedCompletionLocalDateTime ? this.orderModel.formatEstimatedCompletionDateTime(this.recentOrder().placedOrder) : this.orderModel.formatRequiredDateTime(this.recentOrder().initiatedOrder))
                : this.recentOrder().placedOrder.requestId ? this.recentOrder().placedOrder.estimatedCompletionTime
                    : undefined);

    // QR codes disabled for now - this was only shown for Fedelta.  Keeping for possible future use.
    public readonly showOrderNumberQrCode: Signal<boolean> = computed<boolean>(() => this.recentOrder().orderInitiation.orderTypeEnum === OrderType.ORDER_AHEAD && false);

    public readonly simpleRequestHeading: Signal<string> = computed(() =>
        this.configurationModel.isWaiterActor() ? "woa.heading.order.summary.service"
            : this.transferRequestDetails().length === 1 ? "woa.heading.order.summary.simple"
                : undefined);

    async requestInvoice(): Promise<void> {
        return this.navigationService.createPage("woa-invoice-request-page", {
            recentOrder: this.recentOrder()
        });
    }
}
